"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IDistroDotnetSDKProvider = void 0;
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
const fs = require("fs");
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const InstallIdUtilities_1 = require("../Utils/InstallIdUtilities");
const VersionResolver_1 = require("./VersionResolver");
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * This interface describes the functionality needed to manage the .NET SDK on a specific distro and version of Linux.
 *
 * @remarks We accept community contributions of this interface for each distro-version pair.
 * All calls which require sudo must leverage the vscode/sudo library. We will not accept contributions that use other methods to gain admin privilege.
 * Please see DotnetDistroVersion as well to add your version.
 */
class IDistroDotnetSDKProvider {
    constructor(distroVersion, context, utilContext, executor = null) {
        this.distroJson = null;
        this.preinstallCommandKey = 'preInstallCommands';
        this.installCommandKey = 'installCommand';
        this.uninstallCommandKey = 'uninstallCommand';
        this.searchCommandKey = 'searchCommand';
        this.updateCommandKey = 'updateCommand';
        this.packageLookupCommandKey = 'packageLookupCommand';
        this.readSymbolicLinkCommandKey = 'readSymLinkCommand';
        this.currentInstallPathCommandKey = 'currentInstallPathCommand';
        this.isInstalledCommandKey = 'isInstalledCommand';
        this.expectedMicrosoftFeedInstallDirKey = 'expectedMicrosoftFeedInstallDirectory';
        this.expectedDistroFeedInstallDirKey = 'expectedDistroFeedInstallDirectory';
        this.installedSDKVersionsCommandKey = 'installedSDKVersionsCommand';
        this.installedRuntimeVersionsCommandKey = 'installedRuntimeVersionsCommand';
        this.currentInstallVersionCommandKey = 'currentInstallationVersionCommand';
        this.missingPackageNameKey = '{packageName}';
        this.missingPathKey = '{path}';
        this.distroVersionsKey = 'versions';
        this.versionKey = 'version';
        this.dotnetPackagesKey = 'dotnet';
        this.distroPackagesKey = 'packages';
        this.sdkKey = 'sdk';
        this.runtimeKey = 'runtime';
        this.aspNetKey = 'aspnetcore';
        this.isMidFeedInjection = false;
        this.cachedMyVersionPackages = null;
        this.context = context;
        this.distroVersion = distroVersion;
        this.versionResolver = new VersionResolver_1.VersionResolver(context);
        const distroDataFile = path.join(__dirname, 'distro-data', `distro-support.json`);
        try {
            fs.chmodSync(distroDataFile, 0o544);
        }
        catch (error) {
            this.context.eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to chmod +x on .NET folder ${distroDataFile} when marked for deletion.`));
        }
        this.distroJson = JSON.parse(fs.readFileSync(distroDataFile, 'utf8'));
        if (!distroVersion || !this.distroJson || !(this.distroJson[this.distroVersion.distro])) {
            const error = new EventStreamEvents_1.DotnetAcquisitionDistroUnknownError(new EventStreamEvents_1.EventBasedError('DotnetAcquisitionDistroUnknownError', `Automated installation for the distro ${this.distroVersion.distro} is not yet supported.
Please install the .NET SDK manually: https://dotnet.microsoft.com/download.
If you would like to contribute to the list of supported distros, please visit: https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/adding-distros.md`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
            throw error.error;
        }
        const validCommandSet = this.getAllValidCommands();
        this.commandRunner = executor !== null && executor !== void 0 ? executor : new CommandExecutor_1.CommandExecutor(context, utilContext, validCommandSet);
    }
    /**
     *
     * @param fullySpecifiedVersion The version of dotnet to check support for in the 3-part semver version.
     * @returns true if the version is supported by default within the distro, false else.
     */
    isDotnetVersionSupported(fullySpecifiedVersion, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            const supportStatus = yield this.getDotnetVersionSupportStatus(fullySpecifiedVersion, installType);
            const supportedType = supportStatus === "DISTRO" /* DotnetDistroSupportStatus.Distro */ || supportStatus === "MICROSOFT" /* DotnetDistroSupportStatus.Microsoft */;
            return supportedType;
        });
    }
    myVersionPackages(installType_1) {
        return __awaiter(this, arguments, void 0, function* (installType, haveTriedFeedInjectionAlready = false) {
            this.context.eventStream.post(new EventStreamEvents_1.DistroPackagesSearch(`Searching for .NET packages for distro ${this.distroVersion.distro} version ${this.distroVersion.version}: has tried injection? ${haveTriedFeedInjectionAlready}`));
            if (this.cachedMyVersionPackages) {
                this.context.eventStream.post(new EventStreamEvents_1.FoundDistroVersionDetails(`Found cached distro version details: ${JSON.stringify(this.cachedMyVersionPackages)}`));
                return this.cachedMyVersionPackages;
            }
            const availableVersions = [];
            const potentialDotnetPackageNames = this.distroJson[this.distroVersion.distro][this.distroPackagesKey];
            for (const packageSet of potentialDotnetPackageNames) {
                this.context.eventStream.post(new EventStreamEvents_1.DistroPackagesSearch(`Searching for .NET packages for distro ${this.distroVersion.distro} version ${this.distroVersion.version} in package set ${JSON.stringify(packageSet)}`));
                const thisVersionPackage = {
                    version: packageSet[this.versionKey],
                    packages: []
                };
                for (const packageName of packageSet[installType]) {
                    let command = this.myDistroCommands(this.searchCommandKey);
                    command = CommandExecutor_1.CommandExecutor.replaceSubstringsInCommands(command, this.missingPackageNameKey, packageName);
                    const packageIsAvailableResult = (yield this.commandRunner.executeMultipleCommands(command, null, false))[0];
                    packageIsAvailableResult.stdout = packageIsAvailableResult.stdout.trim();
                    packageIsAvailableResult.stderr = packageIsAvailableResult.stderr.trim();
                    packageIsAvailableResult.status = packageIsAvailableResult.status.trim();
                    const packageExists = this.isPackageFoundInSearch(`${packageIsAvailableResult.stdout}${packageIsAvailableResult.stderr}`, packageIsAvailableResult.status);
                    if (packageExists) {
                        thisVersionPackage.packages.push(packageName);
                    }
                }
                if (thisVersionPackage.packages.length !== 0) {
                    availableVersions.push(thisVersionPackage);
                }
            }
            if (availableVersions.length === 0 && !haveTriedFeedInjectionAlready) {
                // PMC is only injected and should only be injected for MSFT feed distros.
                // Our check runs by checking the feature band first, so that needs to be supported for it to fallback to the preinstall command check.
                const fakeVersionToCheckMicrosoftSupportStatus = '8.0.1xx';
                this.context.eventStream.post(new EventStreamEvents_1.FeedInjection(`Starting feed injection after package version searching as no packages could be found.`));
                yield this.injectPMCFeed(fakeVersionToCheckMicrosoftSupportStatus, installType);
                const packagesAfterFeedInjection = yield this.myVersionPackages(installType, true);
                this.context.eventStream.post(new EventStreamEvents_1.FoundDistroVersionDetails(`Caching distro version details after injection: ${JSON.stringify(packagesAfterFeedInjection)}`));
                this.cachedMyVersionPackages = packagesAfterFeedInjection;
            }
            else {
                this.context.eventStream.post(new EventStreamEvents_1.FoundDistroVersionDetails(`Caching distro version details: ${JSON.stringify(this.cachedMyVersionPackages)}`));
                this.cachedMyVersionPackages = availableVersions;
            }
            return this.cachedMyVersionPackages;
        });
    }
    injectPMCFeed(fullySpecifiedVersion, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isMidFeedInjection) {
                this.context.eventStream.post(new EventStreamEvents_1.FeedInjection(`Skipping injection : already started.`));
                return;
            }
            this.isMidFeedInjection = true;
            if (this.myVersionDetails().hasOwnProperty(this.preinstallCommandKey)) {
                this.context.eventStream.post(new EventStreamEvents_1.FeedInjectionStarted(`Configuring your system to allow .NET to be installed. Please wait, this may take a few minutes...`));
                const myVersionDetails = this.myVersionDetails();
                const preInstallCommands = myVersionDetails[this.preinstallCommandKey];
                yield this.commandRunner.executeMultipleCommands(preInstallCommands, {}, false);
                this.context.eventStream.post(new EventStreamEvents_1.FeedInjectionFinished(`The Microsoft Package Manager feed has been added. Proceeding to install .NET.`));
            }
            else {
                this.context.eventStream.post(new EventStreamEvents_1.FeedInjection(`Skipping injection : not Microsoft supported.`));
            }
            this.isMidFeedInjection = false;
        });
    }
    myVersionDetails() {
        const distroVersions = this.distroJson[this.distroVersion.distro][this.distroVersionsKey];
        const versionData = distroVersions.filter((x) => x[this.versionKey] === this.distroVersion.version)[0];
        if (!versionData) {
            const closestVersion = this.findMostSimilarVersion(this.distroVersion.version, distroVersions.map((x) => parseFloat(x[this.versionKey])));
            return distroVersions.filter((x) => parseFloat(x[this.versionKey]) === closestVersion)[0];
        }
        this.context.eventStream.post(new EventStreamEvents_1.FoundDistroVersionDetails(`Found distro version details: ${JSON.stringify(versionData)}`));
        return versionData;
    }
    findMostSimilarVersion(myVersion, knownVersions) {
        const sameMajorVersions = knownVersions.filter(x => Math.floor(x) === Math.floor(parseFloat(myVersion)));
        if (sameMajorVersions && sameMajorVersions.length) {
            this.context.eventStream.post(new EventStreamEvents_1.FoundDistroVersionDetails(`Found similar version details for same major: ${JSON.stringify(sameMajorVersions)}`));
            return Math.max(...sameMajorVersions);
        }
        const lowerMajorVersions = knownVersions.filter(x => x < Math.floor(parseFloat(myVersion)));
        if (lowerMajorVersions && lowerMajorVersions.length) {
            return Math.max(...lowerMajorVersions);
        }
        // Just return the lowest known version, as it will be the closest to our version, as they are all larger than our version.
        return Math.min(...knownVersions);
    }
    myDistroStrings(stringKey) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return this.distroJson[this.distroVersion.distro][stringKey];
    }
    myDistroCommands(commandKey) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return this.distroJson[this.distroVersion.distro][commandKey];
    }
    getAllValidCommands() {
        const validCommands = [];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const baseCommands = (Object.values(this.distroJson[this.distroVersion.distro])
            .filter((x) => x && Array.isArray(x) && (x[0].commandParts))).flat();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        let preInstallCommands = this.myVersionDetails()[this.preinstallCommandKey];
        if (!preInstallCommands) {
            preInstallCommands = [];
        }
        const sudoCommands = baseCommands.concat(preInstallCommands).filter(x => x.runUnderSudo);
        for (const command of sudoCommands) {
            if (command.commandParts.slice(-1)[0] !== this.missingPackageNameKey) {
                validCommands.push(`"${CommandExecutor_1.CommandExecutor.prettifyCommandExecutorCommand(command, false)}"`);
            }
            else {
                for (const packageName of this.allPackages()) {
                    const newCommand = CommandExecutor_1.CommandExecutor.replaceSubstringsInCommands([command], this.missingPackageNameKey, packageName)[0];
                    validCommands.push(`"${CommandExecutor_1.CommandExecutor.prettifyCommandExecutorCommand(newCommand, false)}"`);
                }
            }
        }
        return [...new Set(validCommands)];
    }
    allPackages() {
        let allPackages = [];
        // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const distroPackages = this.distroJson[this.distroVersion.distro][this.distroPackagesKey];
        for (const packageSet of distroPackages) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            allPackages = allPackages.concat(packageSet[this.sdkKey]);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            allPackages = allPackages.concat(packageSet[this.runtimeKey]);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            allPackages = allPackages.concat(packageSet[this.aspNetKey]);
        }
        return allPackages;
    }
    myDotnetVersionPackageName(fullySpecifiedDotnetVersion, installType) {
        return __awaiter(this, void 0, void 0, function* () {
            const myDotnetVersions = yield this.myVersionPackages(installType, this.isMidFeedInjection);
            for (const dotnetPackage of myDotnetVersions) {
                if (dotnetPackage.version === this.JsonDotnetVersion(fullySpecifiedDotnetVersion)) {
                    // Arbitrarily pick the first existing package.
                    this.context.eventStream.post(new EventStreamEvents_1.DistroPackagesSearch(`Found .NET package for version ${fullySpecifiedDotnetVersion} and taking the first: ${JSON.stringify(dotnetPackage.packages)}`));
                    return dotnetPackage.packages[0];
                }
            }
            const err = new EventStreamEvents_1.EventCancellationError('DotnetVersionResolutionError', `Could not find a .NET package for version ${fullySpecifiedDotnetVersion}. Found only: ${JSON.stringify(myDotnetVersions)}`);
            this.context.eventStream.post(new EventStreamEvents_1.DotnetVersionResolutionError(err, (0, InstallIdUtilities_1.getInstallFromContext)(this.context)));
            throw err;
        });
    }
}
exports.IDistroDotnetSDKProvider = IDistroDotnetSDKProvider;
//# sourceMappingURL=IDistroDotnetSDKProvider.js.map